/**
 * Error Parsing Library - Core Parser
 *
 * Parses blockchain transaction errors into user-friendly formats.
 * Handles viem/wagmi errors, AAVE codes, Euler errors, and common EVM errors.
 */

import { BaseError } from 'viem'
import {
  aaveErrors,
  eulerErrorSelectors,
  evmRevertReasons,
  networkErrorPatterns,
  slippageLiquidityPatterns,
  userRejectionPatterns,
} from './errorCodes'
import type { ErrorCategory, ErrorContext, ParsedTransactionError, SuggestedAction } from './types'

/**
 * Parse any error into a structured ParsedTransactionError.
 *
 * @param error - The error to parse (can be Error, string, or unknown)
 * @returns ParsedTransactionError with user-friendly messaging
 */
export function parseTransactionError(error: unknown): ParsedTransactionError {
  // Handle null/undefined
  if (!error) {
    return createUnknownError(new Error('Unknown error'))
  }

  // Convert to Error if needed
  const errorObj = error instanceof Error ? error : new Error(String(error))

  // Check for user rejection first (not really an error)
  if (isUserRejection(errorObj)) {
    return createUserRejectionError(errorObj)
  }

  // Try to parse as viem BaseError for rich error info
  if (errorObj instanceof BaseError) {
    return parseViemError(errorObj)
  }

  // --- Protocol-specific checks first ---
  // Same ordering rationale as parseViemError: broad network patterns like
  // "insufficient funds" can shadow protocol-level revert reasons.

  // Try to extract AAVE error code from message
  const codeError = parseErrorCode(errorObj)
  if (codeError) {
    return codeError
  }

  // Check for Euler error selectors and E_ names in any error message (including cause chain).
  // This catches simulation errors from wagmi-evc that aren't viem BaseError instances.
  const eulerError = parseEulerErrorFromMessage(errorObj)
  if (eulerError) {
    return eulerError
  }

  // Try to match known revert reasons
  const revertError = parseRevertReason(errorObj)
  if (revertError) {
    return revertError
  }

  // Check for slippage/liquidity errors
  const slippageError = parseSlippageLiquidityError(errorObj)
  if (slippageError) {
    return slippageError
  }

  // --- Broad network patterns last ---
  const networkError = parseNetworkError(errorObj)
  if (networkError) {
    return networkError
  }

  // Check for generic "reverted" language or wagmi-evc simulation wrappers
  const fullMessage = collectErrorMessages(errorObj)
  if (/reverted|Simulate EVC Batch Call/i.test(fullMessage)) {
    const msg = errorObj.message || ''
    return {
      originalError: errorObj,
      category: 'simulation',
      severity: 'error',
      userMessage: sanitizeForDisplay(msg, GENERIC_REVERT_MESSAGE),
      technicalMessage: msg,
      isRecoverable: true,
      suggestedAction: 'retry',
      shouldHide: false,
    }
  }

  // Default to unknown error
  return createUnknownError(errorObj)
}

/**
 * Check if an error is a user rejection (user cancelled in wallet).
 */
export function isUserRejection(error: Error | null | undefined): boolean {
  if (!error) return false
  const message = (error.message || '').toLowerCase()
  return userRejectionPatterns.some((pattern) => pattern.test(message))
}

/**
 * Parse a viem BaseError with rich error information.
 */
function parseViemError(error: BaseError): ParsedTransactionError {
  const message = error.message || ''
  const shortMessage = error.shortMessage || message

  // Extract context from viem error
  const context = extractViemErrorContext(error)

  // Collect the full cause chain once for all downstream checks
  const fullViemMessage = collectErrorMessages(error)

  // --- Protocol-specific checks first ---
  // These must run before broad network patterns because viem embeds revert
  // reason text into `message`/`shortMessage`. A contract reverting with e.g.
  // "insufficient funds" would otherwise be swallowed by the network matcher.

  // Check for AAVE error codes in revert message (check both shortMessage and full cause chain)
  const aaveCode = findAaveCode(shortMessage, fullViemMessage)
  if (aaveCode) {
    return {
      originalError: error,
      category: getAaveErrorCategory(aaveCode),
      severity: 'error',
      userMessage: aaveErrors[aaveCode]!,
      technicalMessage: shortMessage,
      errorCode: aaveCode,
      isRecoverable: isAaveErrorRecoverable(aaveCode),
      suggestedAction: getAaveErrorAction(aaveCode),
      context,
      shouldHide: false,
    }
  }

  // Check all hex selectors in the full cause chain against known Euler errors
  const eulerFound = findEulerSelector(fullViemMessage)
  if (eulerFound) {
    return {
      originalError: error,
      category: getEulerErrorCategory(eulerFound.entry.name),
      severity: 'error',
      userMessage: eulerFound.entry.message,
      technicalMessage: shortMessage,
      errorCode: eulerFound.entry.name,
      isRecoverable: isEulerErrorRecoverable(eulerFound.entry.name),
      suggestedAction: getEulerErrorAction(eulerFound.entry.name),
      context: { ...context, errorSelector: eulerFound.selector },
      shouldHide: false,
    }
  }

  // Check for E_ prefixed Euler errors in message (using full cause chain)
  const eulerByName = findEulerName(fullViemMessage)
  if (eulerByName) {
    return {
      originalError: error,
      category: getEulerErrorCategory(eulerByName.name),
      severity: 'error',
      userMessage: eulerByName.message,
      technicalMessage: shortMessage,
      errorCode: eulerByName.name,
      isRecoverable: isEulerErrorRecoverable(eulerByName.name),
      suggestedAction: getEulerErrorAction(eulerByName.name),
      context,
      shouldHide: false,
    }
  }

  // Check for known revert reason strings (EVM, swap adapter, etc.)
  const fullViemMessageLower = fullViemMessage.toLowerCase()
  for (const [pattern, userMessage] of Object.entries(evmRevertReasons)) {
    if (pattern === 'execution reverted') continue
    if (fullViemMessageLower.includes(pattern.toLowerCase())) {
      return {
        originalError: error,
        category: 'reverted',
        severity: 'error',
        userMessage,
        technicalMessage: shortMessage,
        isRecoverable: true,
        suggestedAction: getSuggestedActionForRevert(pattern.toLowerCase()),
        context,
        shouldHide: false,
      }
    }
  }

  // Check for slippage/liquidity errors (also check full cause chain for EVC batch wrappers)
  for (const { pattern, category, message: userMessage } of slippageLiquidityPatterns) {
    if (pattern.test(fullViemMessage)) {
      return {
        originalError: error,
        category,
        severity: 'warning',
        userMessage,
        technicalMessage: shortMessage,
        isRecoverable: true,
        suggestedAction: category === 'slippage' ? 'adjust-slippage' : 'adjust-amount',
        context,
        shouldHide: false,
      }
    }
  }

  // --- Broad network patterns last ---
  // Only reached when no protocol-specific check matched, so revert reasons
  // containing words like "insufficient funds" or "timeout" are not misclassified.
  for (const { pattern, message: userMessage, category, isRecoverable, suggestedAction } of networkErrorPatterns) {
    if (pattern.test(fullViemMessage)) {
      return {
        originalError: error,
        category,
        severity: isRecoverable ? 'warning' : 'error',
        userMessage,
        technicalMessage: shortMessage,
        isRecoverable,
        suggestedAction,
        context,
        shouldHide: false,
      }
    }
  }

  // Check for common simulation/reverted errors
  if (shortMessage.includes('execution reverted') || fullViemMessage.includes('reverted')) {
    return {
      originalError: error,
      category: 'reverted',
      severity: 'error',
      userMessage: sanitizeForDisplay(shortMessage, GENERIC_REVERT_MESSAGE),
      technicalMessage: shortMessage,
      isRecoverable: true,
      suggestedAction: 'retry',
      context,
      shouldHide: false,
    }
  }

  // Default viem error handling
  return {
    originalError: error,
    category: 'unknown',
    severity: 'error',
    userMessage: shortMessage || 'An error occurred with your transaction.',
    technicalMessage: message,
    isRecoverable: true,
    suggestedAction: 'retry',
    context,
    shouldHide: false,
  }
}

/**
 * Extract context information from a viem error.
 */
function extractViemErrorContext(error: BaseError): ErrorContext {
  const context: ErrorContext = {}
  const e = error as unknown as Record<string, unknown>

  // Extract transaction hash if available
  if (typeof e.txHash === 'string') {
    context.txHash = e.txHash
  }

  // Extract contract address
  if (typeof e.contractAddress === 'string') {
    context.contractAddress = e.contractAddress
  }

  // Extract function name
  if (typeof e.functionName === 'string') {
    context.functionName = e.functionName
  }

  // Walk cause chain for additional info
  let cause = e.cause
  let depth = 0
  while (cause && depth < 5) {
    const causeObj = cause as Record<string, unknown>
    if (typeof causeObj.data === 'string') {
      context.rawData = causeObj.data
    }
    cause = causeObj.cause
    depth++
  }

  return context
}

/**
 * Parse network-level errors (RPC, gas, nonce, etc.).
 */
function parseNetworkError(error: Error): ParsedTransactionError | null {
  const fullMessage = collectErrorMessages(error)

  for (const { pattern, message: userMessage, category, isRecoverable, suggestedAction } of networkErrorPatterns) {
    if (pattern.test(fullMessage)) {
      return {
        originalError: error,
        category,
        severity: isRecoverable ? 'warning' : 'error',
        userMessage,
        technicalMessage: error.message || '',
        isRecoverable,
        suggestedAction,
        shouldHide: false,
      }
    }
  }

  return null
}

/**
 * Parse slippage and liquidity errors.
 */
function parseSlippageLiquidityError(error: Error): ParsedTransactionError | null {
  const fullMessage = collectErrorMessages(error)

  for (const { pattern, category, message: userMessage } of slippageLiquidityPatterns) {
    if (pattern.test(fullMessage)) {
      return {
        originalError: error,
        category,
        severity: 'warning',
        userMessage,
        technicalMessage: error.message || '',
        isRecoverable: true,
        suggestedAction: category === 'slippage' ? 'adjust-slippage' : 'adjust-amount',
        shouldHide: false,
      }
    }
  }

  return null
}

/**
 * Parse error codes from error message.
 */
function parseErrorCode(error: Error): ParsedTransactionError | null {
  const message = error.message || ''
  const fullMessage = collectErrorMessages(error)

  const code = findAaveCode(message, fullMessage)
  if (code) {
    return {
      originalError: error,
      category: getAaveErrorCategory(code),
      severity: 'error',
      userMessage: aaveErrors[code]!,
      technicalMessage: message,
      errorCode: code,
      isRecoverable: isAaveErrorRecoverable(code),
      suggestedAction: getAaveErrorAction(code),
      shouldHide: false,
    }
  }

  return null
}

/**
 * Parse common revert reasons from error message.
 */
function parseRevertReason(error: Error): ParsedTransactionError | null {
  const fullMessage = collectErrorMessages(error)
  const lowerMessage = fullMessage.toLowerCase()

  for (const [pattern, userMessage] of Object.entries(evmRevertReasons)) {
    if (pattern === 'execution reverted') continue
    if (lowerMessage.includes(pattern.toLowerCase())) {
      return {
        originalError: error,
        category: 'reverted',
        severity: 'error',
        userMessage,
        technicalMessage: error.message || '',
        isRecoverable: true,
        suggestedAction: getSuggestedActionForRevert(pattern.toLowerCase()),
        shouldHide: false,
      }
    }
  }

  return null
}

/**
 * Extract string props commonly found on viem/wagmi error objects.
 */
function extractErrorFields(obj: Record<string, unknown>, parts: string[]): void {
  for (const key of ['message', 'shortMessage', 'details', 'reason'] as const) {
    if (typeof obj[key] === 'string') parts.push(obj[key] as string)
  }
  // viem ContractFunctionRevertedError stores hex in `data` or `signature`
  if (typeof obj.data === 'string') parts.push(obj.data)
  if (typeof obj.signature === 'string') parts.push(obj.signature)
  // viem may wrap decoded data in an object with `raw`
  if (obj.data && typeof obj.data === 'object') {
    const dataObj = obj.data as Record<string, unknown>
    if (typeof dataObj.raw === 'string') parts.push(dataObj.raw)
  }
}

/**
 * Collect all messages, shortMessages, data, and signatures from an error
 * and its full cause chain into a single string.
 */
function collectErrorMessages(error: Error): string {
  const parts: string[] = []
  const visited = new WeakSet<object>()

  function walk(obj: Record<string, unknown>, depth: number): void {
    if (depth > 8 || visited.has(obj)) return
    visited.add(obj)
    extractErrorFields(obj, parts)

    // Walk cause chain
    if (obj.cause && typeof obj.cause === 'object') {
      walk(obj.cause as Record<string, unknown>, depth + 1)
    } else if (typeof obj.cause === 'string') {
      parts.push(obj.cause)
    }

    // wagmi-evc stores the real viem error in rawError (not cause)
    if (obj.rawError && typeof obj.rawError === 'object') {
      walk(obj.rawError as Record<string, unknown>, depth + 1)
    }
  }

  walk(error as unknown as Record<string, unknown>, 0)
  return parts.join('\n')
}

/**
 * AAVE regex patterns for extracting numeric error codes from error text.
 * Shared between parseViemError and parseErrorCode to avoid divergence.
 */
const aaveCodePatterns: RegExp[] = [
  /execution reverted:\s*(\d{1,2})\b/,
  /(?:revert(?:ed)?)[:\s]+(\d{1,2})\b/,
  /reason:\s*"?(\d{1,2})"?\b/,
  /reverted with reason string '(\d{1,2})'/,
  /Error\(string\).*?["'](\d{1,2})["']/,
]

/**
 * Extract an AAVE numeric error code from one or more text strings.
 * Returns the first matched code, or null if none found.
 */
function findAaveCode(...texts: string[]): string | null {
  for (const text of texts) {
    for (const pattern of aaveCodePatterns) {
      const match = text.match(pattern)
      if (match?.[1] && aaveErrors[match[1]]) {
        return match[1]
      }
    }
  }
  return null
}

/**
 * Find a known Euler error selector from a collected message string.
 * Uses matchAll to check every 8-char hex candidate against the selector map,
 * not just the first match (which could be a function selector or address prefix).
 */
function findEulerSelector(text: string): { selector: string; entry: { name: string; message: string } } | null {
  for (const match of text.matchAll(/0x([0-9a-fA-F]{8})(?![0-9a-fA-F])/g)) {
    const selector = match[1]!.toLowerCase()
    const entry = eulerErrorSelectors[selector]
    if (entry) return { selector, entry }
  }
  return null
}

/**
 * Find a known Euler error by E_ name in a collected message string.
 * Uses matchAll to check every E_ candidate, not just the first.
 */
function findEulerName(text: string): { name: string; message: string } | null {
  for (const match of text.matchAll(/E_(\w+)/g)) {
    const errorName = `E_${match[1]}`
    const entry = Object.values(eulerErrorSelectors).find((e) => e.name === errorName)
    if (entry) return entry
  }
  return null
}

/**
 * Parse Euler error selectors and E_ names from any error (including cause chain).
 * Handles simulation errors from wagmi-evc that aren't viem BaseError instances.
 */
function parseEulerErrorFromMessage(error: Error): ParsedTransactionError | null {
  const fullMessage = collectErrorMessages(error)

  // Check all 8-char hex values in the message against known Euler selectors
  const found = findEulerSelector(fullMessage)
  if (found) {
    return {
      originalError: error,
      category: getEulerErrorCategory(found.entry.name),
      severity: 'error',
      userMessage: found.entry.message,
      technicalMessage: error.message,
      errorCode: found.entry.name,
      isRecoverable: isEulerErrorRecoverable(found.entry.name),
      suggestedAction: getEulerErrorAction(found.entry.name),
      context: { errorSelector: found.selector },
      shouldHide: false,
    }
  }

  // Check for E_ prefixed Euler error names
  const eulerByName = findEulerName(fullMessage)
  if (eulerByName) {
    return {
      originalError: error,
      category: getEulerErrorCategory(eulerByName.name),
      severity: 'error',
      userMessage: eulerByName.message,
      technicalMessage: error.message,
      errorCode: eulerByName.name,
      isRecoverable: isEulerErrorRecoverable(eulerByName.name),
      suggestedAction: getEulerErrorAction(eulerByName.name),
      shouldHide: false,
    }
  }

  return null
}

const GENERIC_REVERT_MESSAGE = 'Transaction simulation failed. The transaction would likely fail on-chain.'

/**
 * Sanitize a raw error message for user display.
 * Strips hex data, ABI encoding noise, and version strings, then
 * returns the cleaned text or a fallback if nothing useful remains.
 */
function sanitizeForDisplay(raw: string, fallback: string): string {
  if (!raw || raw === 'execution reverted') return fallback

  const cleaned = raw
    .replace(/0x[0-9a-fA-F]{8,}/g, '') // long hex blobs
    .replace(/Version: viem@\S+/g, '') // viem version line
    .replace(/Details:.*$/gm, '') // viem Details: line
    .replace(/Docs:.*$/gm, '') // viem Docs: line
    .replace(/\n{2,}/g, '\n') // collapse blank lines
    .trim()

  if (cleaned.length < 5) return fallback
  return cleaned
}

/**
 * Create a user rejection "error" (not really an error).
 */
function createUserRejectionError(error: Error): ParsedTransactionError {
  return {
    originalError: error,
    category: 'user-rejection',
    severity: 'info',
    userMessage: 'Transaction was cancelled.',
    technicalMessage: error.message,
    isRecoverable: true,
    suggestedAction: 'none',
    shouldHide: true, // User rejections should typically be hidden
  }
}

/**
 * Create an unknown error.
 */
function createUnknownError(error: Error): ParsedTransactionError {
  return {
    originalError: error,
    category: 'unknown',
    severity: 'error',
    userMessage: 'An unexpected error occurred. Please try again.',
    technicalMessage: error.message || 'Unknown error',
    isRecoverable: true,
    suggestedAction: 'retry',
    shouldHide: false,
  }
}

// =============================================================================
// Helper functions for determining error metadata
// =============================================================================

function getAaveErrorCategory(code: string): ErrorCategory {
  const num = Number.parseInt(code, 10)

  // Collateral/liquidity related errors
  if ([34, 35, 36, 57, 62].includes(num)) {
    return 'collateral'
  }

  // Insufficient funds/balance errors
  if ([32, 43].includes(num)) {
    return 'insufficient-funds'
  }

  // Cap exceeded errors
  if ([50, 51, 52, 53].includes(num)) {
    return 'liquidity'
  }

  return 'reverted'
}

function isAaveErrorRecoverable(code: string): boolean {
  const num = Number.parseInt(code, 10)
  // Most AAVE errors are user-recoverable by adjusting parameters
  // Only admin/config errors are not recoverable
  const nonRecoverableCodes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 14, 15, 61, 75, 86, 87]
  return !nonRecoverableCodes.includes(num)
}

function getAaveErrorAction(code: string): SuggestedAction {
  const num = Number.parseInt(code, 10)

  // Collateral-related
  if ([34, 35, 36, 57, 62].includes(num)) {
    return 'add-collateral'
  }

  // Zero-amount input error
  if (num === 26) {
    return 'retry'
  }

  // Underlying balance is zero — user needs to deposit funds first
  if (num === 43) {
    return 'add-funds'
  }

  // Withdraw exceeds balance
  if (num === 32) {
    return 'adjust-amount'
  }

  // Cap-related
  if ([50, 51, 52, 53].includes(num)) {
    return 'adjust-amount'
  }

  return 'retry'
}

function getEulerErrorCategory(errorName: string): ErrorCategory {
  if (errorName.includes('Liquidity') || errorName.includes('LTV') || errorName === 'E_AccountLiquidity') {
    return 'collateral'
  }
  if (
    errorName.includes('Balance') ||
    errorName.includes('Cash') ||
    errorName.includes('TransferFromFailed') ||
    errorName.includes('InsufficientAssets')
  ) {
    return 'insufficient-funds'
  }
  if (errorName.includes('Cap')) {
    return 'liquidity'
  }
  if (errorName.includes('Allowance')) {
    return 'submission'
  }
  if (errorName.includes('Oracle') || errorName.includes('Stale') || errorName.includes('Pyth')) {
    return 'simulation'
  }
  if (errorName.includes('Paused') || errorName.includes('Disabled') || errorName.includes('LockdownMode')) {
    return 'reverted'
  }
  return 'reverted'
}

function isEulerErrorRecoverable(errorName: string): boolean {
  const nonRecoverable = [
    'E_TransientState',
    'E_BadAssetReceiver',
    'E_SelfTransfer',
    'E_SelfLiquidation',
    'E_Initialized',
    'E_DeploymentFailed',
    'E_ProxyMetadata',
  ]
  return !nonRecoverable.includes(errorName)
}

function getEulerErrorAction(errorName: string): SuggestedAction {
  switch (errorName) {
    case 'E_AccountLiquidity':
    case 'E_LTVBorrow':
    case 'E_LTVLiquidation':
      return 'add-collateral'
    case 'E_InsufficientBalance':
    case 'E_InsufficientCash':
    case 'E_InsufficientAssets':
    case 'E_TransferFromFailed':
    case 'ERC20InsufficientBalance':
    case 'InsufficientBalance':
      return 'adjust-amount'
    case 'E_InsufficientAllowance':
    case 'ERC20InsufficientAllowance':
      return 'approve-token'
    case 'E_SupplyCapExceeded':
    case 'E_BorrowCapExceeded':
    case 'E_RepayTooMuch':
    case 'E_ExcessiveRepayAmount':
      return 'adjust-amount'
    case 'E_OperationDisabled':
    case 'HTG_VaultPaused':
    case 'EVC_LockdownMode':
    case 'E_LiquidationCoolOff':
    case 'PriceOracle_TooStale':
    case 'Pyth_StalePrice':
    case 'Pyth_NoFreshUpdate':
    case 'PythOracle_StalePythPrice':
      return 'retry-later'
    case 'PythOracle_ConfidenceTooWide':
    case 'PythOracle_ConfidenceIntervalTooWide':
    case 'PythOracle_PriceZero':
    case 'PythOracle_InvalidAnswer':
    case 'PythOracle_Overflow':
    case 'Pyth_InvalidUpdateData':
      return 'retry'
    case 'Pyth_InsufficientFee':
    case 'Pyth_PriceFeedNotFound':
    case 'Pyth_PriceFeedNotFoundWithinRange':
    case 'Pyth_InvalidUpdateDataSource':
    case 'PythOracle_InvalidConfiguration':
      return 'contact-support'
    case 'SwapVerifier_pastDeadline':
    case 'SwapVerifier_skimMin':
      return 'retry'
    case 'E_Unauthorized':
    case 'EVC_NotAuthorized':
    case 'NotAuthorized':
      return 'contact-support'
    default:
      return 'retry'
  }
}

function getSuggestedActionForRevert(pattern: string): SuggestedAction {
  if (pattern.includes('allowance')) return 'approve-token'
  if (pattern.includes('slippage') || pattern.includes('insufficient_amount')) return 'adjust-slippage'
  if (pattern.includes('balance') || pattern.includes('amount')) return 'adjust-amount'
  if (pattern.includes('owner')) return 'contact-support'
  if (pattern.includes('paused')) return 'retry-later'
  return 'retry'
}

/**
 * Decode revert fields from an onchain transaction response into a user-friendly message.
 *
 * Priority:
 *  1. revert_data  → 4-byte selector lookup (Euler, EVC, periphery, ERC20, price oracle, …)
 *  2. revert_reason → AAVE numeric code (e.g. "execution reverted: 32")
 *  3. revert_reason → known EVM revert reason strings
 *  4. revert_reason present but generic ("execution reverted") → static fallback
 *  5. Neither field → null (caller should hide the section)
 */
export function decodeOnchainRevert(revertData?: string, revertReason?: string): string | null {
  if (revertData) {
    // Trim to just the 4-byte selector so the regex lookahead isn't defeated by
    // ABI-encoded arguments that follow immediately (e.g. 0x34373fbc000000…).
    const selector = revertData.slice(0, 10)
    const found = findEulerSelector(selector)
    if (found) return `${found.entry.name}: ${found.entry.message}`
  }

  if (revertReason) {
    const aaveCode = findAaveCode(revertReason)
    if (aaveCode) return aaveErrors[aaveCode]!

    const lower = revertReason.toLowerCase()
    for (const [pattern, userMessage] of Object.entries(evmRevertReasons)) {
      if (pattern === 'execution reverted') continue
      if (lower.includes(pattern.toLowerCase())) return userMessage
    }

    if (revertReason !== 'execution reverted') return revertReason

    return 'Transaction simulation failed'
  }

  return null
}
